import { useState, useEffect, useCallback, useRef } from "react";
import { RotateCwIcon, ClockIcon, StarIcon, MicIcon, TrendingUpIcon, WifiIcon, CheckIcon } from "lucide-react";
import CountdownTimer from "@component/countdown-timer";
import { Button } from "@component/button";
import ReactiveOrb from "@component/reactive-orb";
import AudioLevelIndicator, { type AudioLevelStatus } from "@component/audio-level-indicator";
import SetupChecksPanel from "@component/setup-checks-panel";
import { InterviewConfirmationModal } from "@component/interview-confirmation-modal";
import { PermissionModal } from "@component/media-permission-warning";
import IssuesDialog from "@component/issues-dialog";
import { makeTrainingBugReport, getPersona } from "@service/training";
import { MicrophoneSelector, SpeakerSelector } from "@component/device-selectors";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { SpeechDetectionProvider, useSpeechDetection } from "@context/speech_detection_context";
import type { TrainingCampaign, TrainingPersona } from "@type/training";
import { LANGUAGES, resolveLanguage } from "@type/languages";

const LOW_AUDIO_WARNING_DELAY_MS = 3000;

export interface TrainingSetupPhaseProps {
    persona: TrainingPersona;
    campaign: TrainingCampaign;
    userId: string;
    sessionToken: string;
    sessionDuration?: number | null;
    onBack: () => void;
    onConfirm: () => void;
    confirmLoading?: boolean;
    usedState?: "completed" | "scoring" | null;
}

function BlobWithMic() {
    const { audioLevel } = useSpeechDetection();
    const getVolume = useCallback(() => audioLevel / 100, [audioLevel]);
    return (
        <div className="flex items-center justify-center w-full h-full">
            <ReactiveOrb getVolume={getVolume} isActive={false} isSpeaking={false} />
        </div>
    );
}

function SetupContent({
    persona,
    campaign,
    userId,
    sessionToken,
    sessionDuration,
    onBack,
    onConfirm,
    confirmLoading,
    usedState,
}: TrainingSetupPhaseProps) {
    const [audioLevelStatus, setAudioLevelStatus] = useState<AudioLevelStatus>("idle");
    const [lowAudioWarning, setLowAudioWarning] = useState(false);
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [timedOut, setTimedOut] = useState(false);
    const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState(false);

    const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedInputId, setSelectedInputId] = useState("");
    const [selectedOutputId, setSelectedOutputId] = useState("");

    const [audioFinished, setAudioFinished] = useState(false);
    const introAudioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function initAudio() {
            // Fetch the full persona to get gender + language — custom personas
            // (e.g. GreenSM) may not have these set in the session response, so we
            // call the individual endpoint which reads directly from the DB record.
            let gender: "male" | "female" = persona.gender ?? "female";
            // Language lives on the persona (DB short code: "en"/"id"/"my"/"vn"),
            // not the campaign. Prefer the freshly-fetched persona, then the prop,
            // then the campaign as a last resort.
            let langCode: string | null | undefined = persona.language ?? campaign.language;
            try {
                const fetched = await getPersona(persona.id);
                if (!cancelled && fetched?.gender) gender = fetched.gender;
                if (!cancelled && fetched?.language) langCode = fetched.language;
            } catch { /* fall back to prop gender/language */ }
            if (cancelled) return;

            const lang = resolveLanguage(langCode) ?? LANGUAGES[0];
            const url = lang.setupVoice[gender];
            const audio = new Audio(url);
            introAudioRef.current = audio;
            audio.onended = () => setAudioFinished(true);
            audio.onerror = (e) => {
                console.error("[setup audio] failed to load:", url, e);
                setAudioFinished(true);
            };
            // Delay slightly so the mic permission prompt from SpeechDetectionProvider
            // doesn't block the browser's autoplay activation window.
            const playTimer = setTimeout(() => {
                audio.play().catch((err) => {
                    console.error("[setup audio] play blocked:", err);
                    setAudioFinished(true);
                });
            }, 500);
            if (cancelled) clearTimeout(playTimer);
        }

        initAudio();
        return () => {
            cancelled = true;
            introAudioRef.current?.pause();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [isOnline, setIsOnline] = useState(
        typeof navigator !== "undefined" ? navigator.onLine : true,
    );
    const [downlink, setDownlink] = useState<number | null>(null);
    const [rtt, setRtt] = useState<number | null>(null);
    const [connectionQuality, setConnectionQuality] = useState<"good" | "poor">("good");

    const micGranted =
        audioLevelStatus !== "idle" &&
        audioLevelStatus !== "denied" &&
        audioLevelStatus !== "error";

    useEffect(() => {
        if (!micGranted || audioLevelStatus !== "low") {
            setLowAudioWarning(false);
            return;
        }
        const t = setTimeout(() => setLowAudioWarning(true), LOW_AUDIO_WARNING_DELAY_MS);
        return () => clearTimeout(t);
    }, [audioLevelStatus, micGranted]);

    const showMicWarning = micGranted && lowAudioWarning;

    useEffect(() => {
        if (!micGranted) return;
        navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => {
                const inputs = devices.filter((d) => d.kind === "audioinput");
                const outputs = devices.filter((d) => d.kind === "audiooutput");
                setAudioInputDevices(inputs);
                setAudioOutputDevices(outputs);
                if (inputs.length > 0 && !selectedInputId) setSelectedInputId(inputs[0].deviceId);
                if (outputs.length > 0 && !selectedOutputId) setSelectedOutputId(outputs[0].deviceId);
            })
            .catch(() => {});
    }, [micGranted]); // eslint-disable-line react-hooks/exhaustive-deps

    const readNetwork = useCallback(() => {
        // @ts-expect-error Navigator connection API not in lib.dom yet
        const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (c) {
            setDownlink(typeof c.downlink === "number" ? c.downlink : null);
            setRtt(typeof c.rtt === "number" ? c.rtt : null);
            setConnectionQuality(
                !navigator.onLine ? "poor"
                : typeof c.rtt === "number" && c.rtt > 400 ? "poor"
                : "good",
            );
        }
        setIsOnline(navigator.onLine);
    }, []);

    useEffect(() => {
        readNetwork();
        // @ts-expect-error Navigator connection API not in lib.dom yet
        const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        c?.addEventListener?.("change", readNetwork);
        window.addEventListener("online", readNetwork);
        window.addEventListener("offline", readNetwork);
        return () => {
            c?.removeEventListener?.("change", readNetwork);
            window.removeEventListener("online", readNetwork);
            window.removeEventListener("offline", readNetwork);
        };
    }, [readNetwork]);

    const networkState: "ok" | "warning" | "error" = !isOnline ? "error"
        : connectionQuality === "good" ? "ok"
        : "warning";

    const hasSetupIssues = !micGranted || networkState === "error";

    const networkTitle =
        networkState === "error" ? "No internet"
        : networkState === "warning" ? "Network okay"
        : "Network excellent";

    const networkSubtitle = (() => {
        if (networkState === "error") return "Check your internet connection.";
        const mbps = downlink != null && downlink >= 1 ? `${downlink.toFixed(1)} Mbps` : null;
        if (networkState === "warning") return mbps ? `${mbps}. Try a stronger signal.` : "Try a stronger signal spot.";
        const latency = rtt != null ? (rtt < 100 ? "low latency" : "high latency") : null;
        if (mbps && latency) return `${mbps}, ${latency}`;
        if (mbps) return mbps;
        return "You're online.";
    })();

    const requestMic = async () => {
        try {
            // @ts-expect-error PermissionName not fully typed in lib.dom
            const status = await navigator.permissions?.query?.({ name: "microphone" });
            if (status?.state === "denied") { setHelpModalOpen(true); return; }
        } catch {}
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
        } catch (err) {
            if (err instanceof Error && err.name === "NotAllowedError") setHelpModalOpen(true);
        }
    };

    const selectedInputLabel =
        audioInputDevices.find((d) => d.deviceId === selectedInputId)?.label || "Default Microphone";
    const selectedOutputLabel =
        audioOutputDevices.find((d) => d.deviceId === selectedOutputId)?.label || "Default Speaker";

    return (
        <div className="flex flex-col">
            {/* Back link */}
            <div className="mb-6">
                <button
                    onClick={onBack}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                    ← Back to Roleplay Introduction
                </button>
            </div>

            {/* Header — full width above both panels */}
            <div className="mb-8">
                <h1 className="text-3xl sm:text-[2rem] font-bold text-gray-900 leading-snug mb-3">
                    Roleplay with {persona.name}
                </h1>
                <div className="flex items-center gap-3">
                    {sessionDuration && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 px-3 py-1 text-sm font-medium shrink-0">
                            {sessionDuration} min
                        </span>
                    )}
                    <p className="text-sm text-gray-600 leading-relaxed">{persona.briefing}</p>
                </div>
            </div>

            {/* Two-panel layout — no divider */}
            <div className="flex flex-col md:flex-row gap-10">

                {/* Left panel — Blob tile + devices + audio indicator */}
                <div className="md:w-[55%] flex flex-col gap-4">
                    {/* Camera-style Blob container */}
                    <div className="relative bg-gray-900 rounded-2xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
                        <BlobWithMic />
                        <div className="absolute bottom-3 left-3">
                            <div className="text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg">
                                You
                            </div>
                        </div>
                    </div>

                    {/* Device selectors — horizontal, below the tile */}
                    <div className="flex gap-3">
                        <div className="flex-1 min-w-0">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <div>
                                        <MicrophoneSelector
                                            label={micGranted ? selectedInputLabel : "Microphone"}
                                            className="w-full text-sm"
                                        />
                                    </div>
                                </DropdownMenuTrigger>
                                {micGranted && (
                                    <DropdownMenuContent align="start" className="w-72">
                                        {audioInputDevices.map((d) => (
                                            <DropdownMenuItem
                                                key={d.deviceId}
                                                onClick={() => setSelectedInputId(d.deviceId)}
                                                className={d.deviceId === selectedInputId ? "bg-blue-50 text-blue-700 font-medium" : ""}
                                            >
                                                <span className="flex-1">{d.label || `Microphone ${d.deviceId.slice(0, 5)}`}</span>
                                                {d.deviceId === selectedInputId && <CheckIcon className="h-4 w-4 text-blue-600 ml-2 shrink-0" />}
                                            </DropdownMenuItem>
                                        ))}
                                        {audioInputDevices.length === 0 && (
                                            <DropdownMenuItem disabled>No microphones found.</DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                )}
                            </DropdownMenu>
                        </div>
                        <div className="flex-1 min-w-0">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <div>
                                        <SpeakerSelector
                                            label={micGranted && audioOutputDevices.length > 0 ? selectedOutputLabel : "Speaker"}
                                            className="w-full text-sm"
                                        />
                                    </div>
                                </DropdownMenuTrigger>
                                {micGranted && audioOutputDevices.length > 0 && (
                                    <DropdownMenuContent align="start" className="w-72">
                                        {audioOutputDevices.map((d) => (
                                            <DropdownMenuItem
                                                key={d.deviceId}
                                                onClick={() => setSelectedOutputId(d.deviceId)}
                                                className={d.deviceId === selectedOutputId ? "bg-blue-50 text-blue-700 font-medium" : ""}
                                            >
                                                <span className="flex-1">{d.label || `Speaker ${d.deviceId.slice(0, 5)}`}</span>
                                                {d.deviceId === selectedOutputId && <CheckIcon className="h-4 w-4 text-blue-600 ml-2 shrink-0" />}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                )}
                            </DropdownMenu>
                        </div>
                    </div>

                    {/* Audio level indicator with rounded border */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                        <AudioLevelIndicator
                            deviceId={selectedInputId || undefined}
                            onStatusChange={setAudioLevelStatus}
                        />
                    </div>
                </div>

                {/* Right panel — tips + setup checks + CTA */}
                <div className="md:w-[45%] flex flex-col gap-6">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            Get ready for your roleplay
                        </h2>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <StarIcon className="h-4 w-4 text-blue-600 shrink-0" />
                                <span className="text-sm text-gray-700">Read the scenario carefully before you begin</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <MicIcon className="h-4 w-4 text-blue-600 shrink-0" />
                                <span className="text-sm text-gray-700">Speak naturally and treat it like a real customer call</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <TrendingUpIcon className="h-4 w-4 text-blue-600 shrink-0" />
                                <span className="text-sm text-gray-700">Every attempt is scored to help track your progress</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <WifiIcon className="h-4 w-4 text-blue-600 shrink-0" />
                                <span className="text-sm text-gray-700">Find a quiet spot with a stable internet connection</span>
                            </div>
                        </div>
                    </div>

                    {!timedOut && (
                        <SetupChecksPanel
                            title="Setup checks"
                            rows={[
                                {
                                    key: "mic",
                                    ok: micGranted && !showMicWarning,
                                    variant: showMicWarning ? "warning" : "error",
                                    title: !micGranted
                                        ? "Microphone permission needed"
                                        : showMicWarning
                                        ? "No audio detected"
                                        : "Microphone enabled",
                                    subtitle: !micGranted
                                        ? "Allow mic access so the AI can hear you."
                                        : showMicWarning
                                        ? "Try selecting a different microphone."
                                        : "We can hear you clearly.",
                                    onEnable: !micGranted ? requestMic : undefined,
                                    enableLabel: !micGranted ? "Enable" : undefined,
                                },
                                {
                                    key: "network",
                                    ok: networkState === "ok",
                                    variant: networkState === "warning" ? "warning" : "error",
                                    title: networkTitle,
                                    subtitle: networkSubtitle,
                                    onEnable: networkState === "warning" ? readNetwork : undefined,
                                    enableLabel: networkState === "warning" ? (
                                        <>
                                            <span className="hidden sm:block">Test again</span>
                                            <RotateCwIcon className="h-3 w-3" strokeWidth={2.5} />
                                        </>
                                    ) : undefined,
                                },
                            ]}
                            timeToJoinLabel="Time to join"
                            timeToJoinElement={
                                !audioFinished ? undefined : (
                                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600">
                                        <ClockIcon className="h-3.5 w-3.5" />
                                        <CountdownTimer
                                            initialMinutes={2}
                                            onTimeout={() => setTimedOut(true)}
                                            className="inline-block"
                                        />
                                    </span>
                                )
                            }
                        />
                    )}

                    {timedOut && (
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 w-full">
                            <p className="text-sm text-amber-800">
                                Your roleplay session has expired because you did not join in time.
                                <br />
                                Refresh this page to try again or come back later when you're ready.
                            </p>
                        </div>
                    )}

                    <div className="flex flex-col items-center gap-3">
                        {timedOut ? (
                            <Button
                                className="w-full py-[21px] rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => window.location.reload()}
                            >
                                Refresh page
                            </Button>
                        ) : (
                            <Button
                                className="w-full py-[21px] rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => setShowConfirmModal(true)}
                                disabled={!audioFinished || confirmLoading || hasSetupIssues}
                            >
                                {!audioFinished
                                    ? "Awaiting AI Roleplay Coach..."
                                    : hasSetupIssues
                                    ? "Resolve the issues above to start"
                                    : "Begin Roleplay"}
                            </Button>
                        )}
                        {usedState === "scoring" && (
                            <div className="text-sm text-amber-700 text-center">
                                This session is being scored. Results will be ready shortly.
                            </div>
                        )}
                        {usedState === "completed" && (
                            <div className="text-sm text-gray-600 text-center">
                                This link has already been used.
                            </div>
                        )}
                        <Button
                            className="w-full py-[21px] rounded-full bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-50"
                            onClick={() => setIsIssuesDialogOpen(true)}
                        >
                            Having issues?
                        </Button>
                        <p className="text-xs text-gray-400 text-center leading-relaxed pt-1">
                            ourteam uses generative AI to power your roleplay partner.
                            <br />
                            Your roleplay is used only to coach you and is never used to train AI models.
                        </p>
                    </div>
                </div>
            </div>

            <PermissionModal
                isOpen={helpModalOpen}
                onClose={() => setHelpModalOpen(false)}
                permLower="microphone"
                permTitle="Microphone"
            />

            <IssuesDialog
                isOpen={isIssuesDialogOpen}
                onClose={() => setIsIssuesDialogOpen(false)}
                onSubmit={() => setIsIssuesDialogOpen(false)}
                userId={userId}
                selectedAudioDeviceID={selectedInputId || undefined}
                type="waiting-room"
                context="roleplay"
                makeReport={(data) => makeTrainingBugReport(sessionToken, {
                    bug_type: data.bug_type,
                    bug_description: data.bug_description,
                    meta: data.meta as Record<string, unknown>,
                })}
            />

            {showConfirmModal && (
                <InterviewConfirmationModal
                    language="Training"
                    companyName={campaign.name}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={() => {
                        setShowConfirmModal(false);
                        onConfirm();
                    }}
                    loading={confirmLoading}
                />
            )}
        </div>
    );
}

export default function TrainingSetupPhase(props: TrainingSetupPhaseProps) {
    return (
        <SpeechDetectionProvider volumeThreshold={30} silenceDelay={400} autoStart={true}>
            <SetupContent {...props} />
        </SpeechDetectionProvider>
    );
}
